import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Calculator, 
  LogOut,
  ArrowLeft,
  Loader2,
  Save,
  Umbrella,
  Trash2,
  Plus
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminIRSYouth = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);

  // Default brackets for new config
  const defaultBrackets = [
    { year: 1, exemption_percentage: 100 },
    { year: 2, exemption_percentage: 75 },
    { year: 3, exemption_percentage: 75 },
    { year: 4, exemption_percentage: 50 },
    { year: 5, exemption_percentage: 50 },
    { year: 6, exemption_percentage: 25 },
    { year: 7, exemption_percentage: 25 },
    { year: 8, exemption_percentage: 25 },
    { year: 9, exemption_percentage: 25 },
    { year: 10, exemption_percentage: 25 },
  ];

  const [formData, setFormData] = useState({
    name: "IRS Jovem 2024",
    description: "Isenção parcial de IRS para jovens até 35 anos",
    max_age: 35,
    max_years: 10,
    brackets: defaultBrackets,
    active: true
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await axios.get(`${API}/irs-jovem`, { headers: getAuthHeaders() });
      setConfigs(response.data);
      
      // If there's an active config, load it for editing
      const activeConfig = response.data.find(c => c.active);
      if (activeConfig) {
        setEditingConfig(activeConfig);
        setFormData({
          name: activeConfig.name,
          description: activeConfig.description || "",
          max_age: activeConfig.max_age,
          max_years: activeConfig.max_years,
          brackets: activeConfig.brackets || defaultBrackets,
          active: activeConfig.active
        });
      }
    } catch (error) {
      console.error("Error loading configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const handleBracketChange = (year, value) => {
    setFormData(prev => ({
      ...prev,
      brackets: prev.brackets.map(b => 
        b.year === year ? { ...b, exemption_percentage: parseFloat(value) || 0 } : b
      )
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        max_age: parseInt(formData.max_age),
        max_years: parseInt(formData.max_years)
      };

      if (editingConfig?.id && editingConfig.id !== "default") {
        await axios.put(`${API}/irs-jovem/${editingConfig.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Configuração atualizada!");
      } else {
        await axios.post(`${API}/irs-jovem`, payload, { headers: getAuthHeaders() });
        toast.success("Configuração criada!");
      }
      
      loadConfigs();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao guardar";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja eliminar esta configuração?")) return;
    
    try {
      await axios.delete(`${API}/irs-jovem/${id}`, { headers: getAuthHeaders() });
      toast.success("Configuração eliminada!");
      loadConfigs();
      
      // Reset form if we deleted the one being edited
      if (editingConfig?.id === id) {
        setEditingConfig(null);
        setFormData({
          name: "IRS Jovem 2024",
          description: "",
          max_age: 35,
          max_years: 10,
          brackets: defaultBrackets,
          active: true
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao eliminar";
      toast.error(message);
    }
  };

  const handleNewConfig = () => {
    setEditingConfig(null);
    setFormData({
      name: "IRS Jovem " + new Date().getFullYear(),
      description: "",
      max_age: 35,
      max_years: 10,
      brackets: defaultBrackets,
      active: true
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-irs-youth">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">IRS Jovem</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{user?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
                <Umbrella className="w-6 h-6 text-purple-500" />
                IRS Jovem
              </h1>
              <p className="text-muted-foreground">Configurar escalões de isenção IRS Jovem</p>
            </div>
          </div>
          <Button onClick={handleNewConfig} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Nova Configuração
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Info Box */}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <p className="text-sm text-purple-800">
                  <strong>IRS Jovem</strong> é um benefício fiscal português que permite aos jovens trabalhadores 
                  (até 35 anos) uma isenção parcial de IRS durante os primeiros anos de trabalho qualificado.
                  Os valores de isenção são aplicados automaticamente nas simulações quando o utilizador ativa esta opção.
                </p>
              </CardContent>
            </Card>

            {/* Existing Configs */}
            {configs.length > 0 && (
              <Card className="bg-white border border-border/60 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Configurações Existentes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {configs.map(config => (
                      <div 
                        key={config.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          editingConfig?.id === config.id ? "bg-purple-50 border-purple-200" : "bg-slate-50 border-border"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${config.active ? "bg-brand-500" : "bg-gray-300"}`} />
                          <div>
                            <p className="font-medium">{config.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Idade máx: {config.max_age} | Anos: {config.max_years}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingConfig(config);
                              setFormData({
                                name: config.name,
                                description: config.description || "",
                                max_age: config.max_age,
                                max_years: config.max_years,
                                brackets: config.brackets || defaultBrackets,
                                active: config.active
                              });
                            }}
                          >
                            Editar
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(config.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Edit Form */}
            <Card className="bg-white border border-border/60 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">
                  {editingConfig?.id && editingConfig.id !== "default" ? "Editar Configuração" : "Nova Configuração"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="IRS Jovem 2024"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <Label>Configuração Ativa</Label>
                    <Switch
                      checked={formData.active}
                      onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrição</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição da configuração"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Idade Máxima</Label>
                    <Input
                      type="number"
                      value={formData.max_age}
                      onChange={(e) => setFormData({ ...formData, max_age: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Máximo de Anos</Label>
                    <Input
                      type="number"
                      value={formData.max_years}
                      onChange={(e) => setFormData({ ...formData, max_years: e.target.value })}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <Label className="text-base font-semibold">Escalões de Isenção (%)</Label>
                  <div className="grid grid-cols-5 gap-3">
                    {formData.brackets.map(bracket => (
                      <div key={bracket.year} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Ano {bracket.year}</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="font-mono pr-8"
                            value={bracket.exemption_percentage}
                            onChange={(e) => handleBracketChange(bracket.year, e.target.value)}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Define a percentagem de isenção de IRS para cada ano de aplicação do benefício.
                  </p>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A guardar...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configuração
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminIRSYouth;
